// src/pages/StudentDashboard.jsx
import React, { useMemo, useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase-config";

const FB_MESSENGER_LINK = "https://m.me/867245013147706";

export default function StudentDashboard({ courses = [], studentProfile, adminPreview = false }) {
  const navigate = useNavigate();

  const [showPolicyPopup, setShowPolicyPopup] = useState(true);
  const [lessonProgress, setLessonProgress] = useState({});

  const authorizedCourses = useMemo(
    () => studentProfile?.authorizedCourses || [],
    [studentProfile]
  );

  const hasAiAccess = studentProfile?.hasAiAccess || false;

  const sortedCourses = useMemo(() => {
    if (!courses.length) return [];

    const granted = [];
    const notGranted = [];

    courses.forEach((course) => {
      if (authorizedCourses.includes(course.id)) {
        granted.push(course);
      } else {
        notGranted.push(course);
      }
    });

    return [...granted, ...notGranted];
  }, [courses, authorizedCourses]);

  const getTotalLessons = (course) => {
    if (!course) return 0;

    if (Array.isArray(course.lessons)) return course.lessons.length;

    if (Array.isArray(course.folders)) {
      return course.folders.reduce((total, folder) => {
        return total + (Array.isArray(folder.lessons) ? folder.lessons.length : 0);
      }, 0);
    }

    return 0;
  };

  const getTimeValue = (item) => {
    if (!item) return 0;
    if (typeof item.updatedAtMs === "number") return item.updatedAtMs;
    if (typeof item.updatedAt === "number") return item.updatedAt;
    if (item.updatedAt?.seconds) return item.updatedAt.seconds * 1000;
    return 0;
  };

  const getCourseProgress = (course) => {
    const courseProgress = Object.values(lessonProgress).filter(
      (item) => item.courseId === course.id
    );

    const completedLessons = courseProgress.filter((item) => item.completed).length;
    const startedLessons = courseProgress.length;

    const totalFromCourse = getTotalLessons(course);
    const totalLessons = totalFromCourse > 0 ? totalFromCourse : startedLessons;

    const totalCompletion = courseProgress.reduce((sum, item) => {
      return sum + Math.min(100, Math.max(0, Number(item.completionPercent || 0)));
    }, 0);

    const percent =
      totalLessons > 0 ? Math.min(100, Math.round(totalCompletion / totalLessons)) : 0;

    const lastWatched = [...courseProgress].sort((a, b) => {
      return getTimeValue(b) - getTimeValue(a);
    })[0];

    return {
      percent,
      completedLessons,
      totalLessons,
      startedLessons,
      lastWatched,
    };
  };

  useEffect(() => {
    if (adminPreview) {
      setLessonProgress({});
      return;
    }

    const loadLessonProgress = async () => {
      try {
        const getMyLessonProgress = httpsCallable(
          functions,
          "getMyLessonProgress"
        );

        const result = await getMyLessonProgress();

        const progressList = Array.isArray(result?.data?.progress)
          ? result.data.progress
          : [];

        const progressMap = {};

        progressList.forEach((item) => {
          progressMap[item.id] = item;
        });

        setLessonProgress(progressMap);
      } catch (err) {
        console.error("Dashboard lesson progress failed:", err);
      }
    };

    loadLessonProgress();
  }, [adminPreview]);

  useEffect(() => {
    if (adminPreview) return;

    const heartbeatStudent = httpsCallable(functions, "heartbeatStudent");

    heartbeatStudent().catch((err) => {
      console.error("Dashboard heartbeat failed:", err);
    });

    const interval = setInterval(() => {
      heartbeatStudent().catch((err) => {
        console.error("Dashboard heartbeat failed:", err);
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [adminPreview]);

  const handleLogout = async () => {
    if (adminPreview) {
      navigate("/admin/dashboard");
      return;
    }

    await signOut(auth);
    navigate("/login");
  };

  const coursePath = (courseId) =>
    adminPreview
      ? `/admin/student-preview/course/${courseId}`
      : `/student/course/${courseId}`;

  const mockInterviewPath = adminPreview
    ? "/admin/student-preview/mock-interview"
    : "/student/mock-interview";

  return (
    <div className="min-h-screen bg-gray-900 text-white px-6 py-8 md:px-12">
      {adminPreview && (
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-orange-500/50 bg-orange-950/40 p-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-orange-300">
              Admin Preview Mode
            </p>
            <p className="mt-1 text-sm text-gray-200">
              You are viewing the student dashboard with access to every course.
              Student heartbeat and progress loading are disabled on this preview page.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="rounded-xl bg-orange-500 px-5 py-2.5 font-black text-white transition hover:bg-orange-600"
          >
            ← Back to Admin Dashboard
          </button>
        </div>
      )}

      {showPolicyPopup && !adminPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-red-500/60 rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="text-center">
              <div className="text-5xl mb-4">⚠️</div>

              <h2 className="text-2xl font-black text-red-400 mb-3">
                Important Course Policy
              </h2>

              <p className="text-gray-200 text-sm leading-relaxed mb-4">
                Downloading, recording, screen recording, sharing, copying, or
                distributing any VA Academy Pro course videos is strictly prohibited.
              </p>

              <div className="bg-red-950/40 border border-red-500/40 rounded-xl p-4 text-left mb-5">
                <p className="text-sm text-red-200 font-semibold mb-2">
                  Please be reminded:
                </p>

                <ul className="text-xs text-gray-300 space-y-2 list-disc pl-5">
                  <li>All video access is monitored.</li>
                  <li>Your IP address, country, and video activity are logged.</li>
                  <li>Suspicious activity may trigger a temporary account ban.</li>
                  <li>Repeated violations may result in permanent account removal.</li>
                </ul>
              </div>

              <button
                onClick={() => setShowPolicyPopup(false)}
                className="w-full bg-orange-600 hover:bg-orange-500 py-3 rounded-xl font-black transition active:scale-95"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="flex justify-between items-center mb-10 border-b border-gray-700 pb-4">
        <h1 className="text-3xl font-bold text-orange-400">
          Student Dashboard
        </h1>

        {!adminPreview && (
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 px-5 py-2 rounded-lg font-semibold transition active:scale-95"
          >
            Logout
          </button>
        )}
      </header>

      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-2xl font-semibold">Skill Practice</h2>
          <span className="bg-blue-500/20 text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-500/50 uppercase tracking-widest animate-pulse">
            New Feature
          </span>
        </div>

        <div
          className={`relative overflow-hidden border rounded-3xl p-8 shadow-2xl group transition-all duration-500 ${
            hasAiAccess
              ? "bg-gradient-to-br from-gray-800 to-indigo-950 border-blue-500/30"
              : "bg-gray-800/50 border-gray-700 opacity-80"
          }`}
        >
          {hasAiAccess && (
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
          )}

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center border transition-all ${
                  hasAiAccess
                    ? "bg-blue-500/20 border-blue-400/30"
                    : "bg-gray-700 border-gray-600"
                }`}
              >
                <span className={`text-3xl ${!hasAiAccess && "grayscale opacity-50"}`}>
                  🤖
                </span>
              </div>

              <div>
                <h3 className={`text-2xl font-bold ${!hasAiAccess ? "text-gray-400" : "text-white"}`}>
                  AI Mock Interview Coach
                </h3>

                <p className={`mt-1 max-w-md ${!hasAiAccess ? "text-gray-500" : "text-gray-400"}`}>
                  {hasAiAccess
                    ? "Master your Discovery Calls with real-time voice analysis and personalized VA coaching."
                    : "Advanced AI voice training is currently locked. Complete your core modules to gain access."}
                </p>
              </div>
            </div>

            {hasAiAccess ? (
              <button
                onClick={() => navigate(mockInterviewPath)}
                className="w-full md:w-auto px-8 py-4 font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-500 transition-all active:scale-95"
              >
                Start Practice Session
              </button>
            ) : (
              <a
                href={FB_MESSENGER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full md:w-auto px-8 py-4 font-bold text-gray-400 bg-gray-700 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all flex items-center justify-center gap-2"
              >
                Inquire Access
              </a>
            )}
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold mb-6">Your Courses</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {sortedCourses.map((course) => {
          const hasAccess = authorizedCourses.includes(course.id);
          const progress = getCourseProgress(course);

          return (
            <div
              key={course.id}
              className={`flex flex-col rounded-2xl border overflow-hidden transition-all duration-300 ${
                hasAccess
                  ? "bg-gray-800 border-green-600 shadow-lg shadow-green-900/10"
                  : "bg-gray-800 border-gray-600 opacity-90"
              }`}
            >
              <div className="relative h-40 w-full overflow-hidden">
                {course.coverImage && (
                  <img
                    src={course.coverImage}
                    alt={course.name}
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      !hasAccess ? "grayscale blur-[1px]" : "grayscale-0"
                    }`}
                  />
                )}

                {!hasAccess && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="bg-black/60 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border border-gray-500">
                      🔒 Locked
                    </span>
                  </div>
                )}

                {hasAccess && progress.percent === 100 && (
                  <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-black shadow-lg">
                    ✔ Completed
                  </div>
                )}
              </div>

              <div className="flex flex-col flex-1 p-5">
                <h3 className={`text-xl font-bold mb-2 ${!hasAccess ? "text-gray-400" : "text-white"}`}>
                  {course.name}
                </h3>

                <p className="text-sm text-gray-400 mb-4 line-clamp-2 italic">
                  {course.description || "Premium VA Training Module"}
                </p>

                {hasAccess && (
                  <div className="mb-5">
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Course Progress</span>
                      <span>{progress.percent}%</span>
                    </div>

                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${progress.percent}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-xs text-gray-400 mt-2">
                      <span>
                        {progress.completedLessons} / {progress.totalLessons || 0} lessons completed
                      </span>

                      {progress.startedLessons > 0 && (
                        <span>{progress.startedLessons} started</span>
                      )}
                    </div>

                    {progress.lastWatched?.lessonTitle && (
                      <div className="mt-3 bg-gray-900/70 border border-gray-700 rounded-lg p-3">
                        <p className="text-[11px] text-gray-500 uppercase tracking-widest mb-1">
                          Continue Learning
                        </p>
                        <p className="text-sm text-gray-300 line-clamp-1">
                          {progress.lastWatched.lessonTitle}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {hasAccess ? (
                  <button
                    onClick={() => navigate(coursePath(course.id))}
                    className="w-full bg-green-600 hover:bg-green-700 py-2 rounded-lg font-semibold mt-auto transition shadow-md active:scale-95"
                  >
                    {progress.startedLessons > 0 ? "Continue Learning" : "Open Full Course"}
                  </button>
                ) : (
                  <a
                    href={FB_MESSENGER_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center bg-orange-500 hover:bg-orange-600 py-2 rounded-lg font-semibold mt-auto transition shadow-md active:scale-95"
                  >
                    Inquire Now
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}